import React from 'react';
import { Badge, Tag } from 'antd';
import ColorHash from 'color-hash';

import CropNameBadge from '../../components/CropNameBadge';

import '../../less/FarmProductionRecordDaySummary.less';

const colorHash = new ColorHash();

const typeLabels = {
  SOW: 'Sowing',
  TRANSPLANT: 'Transplanting',
  HARVEST: 'Harvesting',
  WASTE: 'Wastage',
};

const typeTagColors = {
  SOW: 'blue',
  TRANSPLANT: 'green',
  HARVEST: 'orange',
  WASTE: 'default',
};

const typeOrder = ['SOW', 'TRANSPLANT', 'HARVEST', 'WASTE'];

const sourceLabels = {
  LOCAL: 'Local',
  EXTERNAL: 'External',
};

const sourceOrder = ['LOCAL', 'EXTERNAL'];

const stageLabels = {
  PROPAGATION: 'Propagation',
  PRODUCTION: 'Production',
};

const stageOrder = ['PROPAGATION', 'PRODUCTION'];

const formatCount = (n) => Number(n).toLocaleString();

const formatWeight = (grams) => {
  if (grams == null || grams <= 0) {
    return null;
  }
  if (grams >= 1000) {
    const kg = grams / 1000;
    const rounded = kg >= 100 ? Math.round(kg) : Math.round(kg * 10) / 10;
    return `${formatCount(rounded)} kg`;
  }
  return `${formatCount(Math.round(grams))} g`;
};

const buildAggregateLines = (type, records) => {
  const buckets = {};

  for (const record of records) {
    let key;
    let label;

    if (type === 'HARVEST') {
      key = 'all';
      label = null;
    } else if (type === 'WASTE') {
      key = record.stage || '';
      label = stageLabels[record.stage] || null;
    } else {
      const source = record.source || 'LOCAL';
      key = source;
      label = sourceLabels[source] || source;
    }

    if (!buckets[key]) {
      buckets[key] = {
        key,
        label,
        amount: 0,
        weight: 0,
        hasWeight: false,
      };
    }

    buckets[key].amount += record.amount || 0;
    if (record.weight != null) {
      buckets[key].weight += record.weight;
      buckets[key].hasWeight = true;
    }
  }

  const values = Object.values(buckets);

  if (type === 'WASTE') {
    values.sort((a, b) => {
      const orderA = stageOrder.indexOf(a.key);
      const orderB = stageOrder.indexOf(b.key);
      return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
    });
  } else if (type !== 'HARVEST') {
    values.sort((a, b) => {
      const orderA = sourceOrder.indexOf(a.key);
      const orderB = sourceOrder.indexOf(b.key);
      return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
    });
  }

  return values;
};

const buildTypeSections = (records) => {
  const byType = {};

  for (const record of records) {
    if (!byType[record.type]) {
      byType[record.type] = [];
    }
    byType[record.type].push(record);
  }

  return typeOrder
    .filter(type => byType[type])
    .map(type => ({
      type,
      lines: buildAggregateLines(type, byType[type]),
    }));
};

const buildCropCards = (records) => {
  const byCrop = {};

  for (const record of records) {
    const cropId = record.cropId;
    if (!byCrop[cropId]) {
      byCrop[cropId] = {
        cropId,
        name: record.cropName || (record.crop && record.crop.name) || '-',
        records: [],
      };
    }
    byCrop[cropId].records.push(record);
  }

  return Object.values(byCrop)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(crop => ({
      ...crop,
      types: buildTypeSections(crop.records),
    }));
};

const buildZoneGroups = (records) => {
  const byZone = {};

  for (const record of records) {
    const zoneKey = record.zone || '';
    if (!byZone[zoneKey]) {
      byZone[zoneKey] = [];
    }
    byZone[zoneKey].push(record);
  }

  const namedZones = Object.keys(byZone)
    .filter(zone => zone)
    .sort((a, b) => a.localeCompare(b));

  const zones = namedZones.map(zone => ({
    zone,
    crops: buildCropCards(byZone[zone]),
  }));

  if (byZone['']) {
    zones.push({
      zone: null,
      crops: buildCropCards(byZone['']),
    });
  }

  return zones;
};

export default function FarmProductionRecordDaySummary({ records }) {
  const zones = buildZoneGroups(records || []);
  const hideNoZoneHeader = zones.length === 1 && zones[0].zone === null;

  if (!zones.length) {
    return <div className="farm-production-record-day-summary empty">No records</div>;
  }

  return (
    <div className="farm-production-record-day-summary">
      {zones.map(zoneGroup => {
        const showHeader = !(hideNoZoneHeader && zoneGroup.zone === null);
        return (
          <div key={zoneGroup.zone || '__no_zone'} className="zone-section">
            {showHeader && (
              <div className="zone-header">
                <span className="zone-label">Zone</span>
                {zoneGroup.zone ? (
                  <Badge count={zoneGroup.zone} style={{ backgroundColor: colorHash.hex(zoneGroup.zone) }} />
                ) : (
                  <span className="zone-no-zone">No zone</span>
                )}
              </div>
            )}
            <div className="crop-grid">
              {zoneGroup.crops.map(crop => (
                <div key={crop.cropId} className="crop-card">
                  <div className="crop-card-header">
                    <span className="crop-name"><CropNameBadge name={crop.name} /></span>
                  </div>
                  <div className="type-rows">
                    {crop.types.map(typeSection => (
                      typeSection.lines.map((line, index) => {
                        const weightLabel = line.hasWeight ? formatWeight(line.weight) : null;
                        return (
                          <div key={`${typeSection.type}-${line.key}`} className="type-row">
                            <div className="type-row-type">
                              {index === 0 && (
                                <Tag color={typeTagColors[typeSection.type]}>{typeLabels[typeSection.type]}</Tag>
                              )}
                            </div>
                            <div className="type-row-label">{line.label || ''}</div>
                            <div className="type-row-values">
                              <div className="type-row-amount">{formatCount(line.amount)}</div>
                              {weightLabel && (
                                <div className="type-row-weight">{weightLabel}</div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
